import { useEffect, useState, type RefObject } from 'react';

/**
 * A hundred tiles means a hundred blobs. `loading="lazy"` doesn't help — the
 * cost here is object URLs and decoded bitmaps, not network. So a tile only
 * asks `useImageUrl` for its photo once it's near the viewport, and drops the
 * reference again once it's well past.
 *
 * One observer for the whole board, not one per tile: five hundred
 * IntersectionObservers is itself the performance problem.
 */

const NEAR_MARGIN = '800px';

/**
 * Leaving the margin doesn't release immediately. Scrolling back and forth
 * across the boundary would otherwise revoke and recreate the same URL a
 * dozen times a second, which is worse than holding it.
 *
 * Kept short, though: a flick through a long board crosses hundreds of tiles
 * and each one holds a decoded bitmap until its timer fires. 600ms is long
 * enough to absorb jitter at the boundary and short enough that a fling
 * doesn't leave a whole board's worth of photos live behind it.
 */
const RELEASE_MS = 600;

type Listener = (intersecting: boolean) => void;

const listeners = new Map<Element, Listener>();
let shared: IntersectionObserver | undefined;

function observer(): IntersectionObserver {
  shared ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) listeners.get(entry.target)?.(entry.isIntersecting);
    },
    { rootMargin: NEAR_MARGIN },
  );
  return shared;
}

export function useNearViewport(ref: RefObject<HTMLElement | null>): boolean {
  const [near, setNear] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (node === null) return;

    let timer: number | undefined;
    const io = observer();

    listeners.set(node, (intersecting) => {
      if (intersecting) {
        window.clearTimeout(timer);
        timer = undefined;
        setNear(true);
      } else if (timer === undefined) {
        timer = window.setTimeout(() => setNear(false), RELEASE_MS);
      }
    });

    io.observe(node);
    return () => {
      window.clearTimeout(timer);
      io.unobserve(node);
      listeners.delete(node);
    };
  }, [ref]);

  return near;
}
